import { ChevronDown } from 'lucide-react';
import { useId } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/Collapsible/Collapsible';
import { cn } from '@/shared/utils/cn/cn.util';

import { FormFieldset } from '../FormFieldset/FormFieldset';
import { FormLegendLabel } from '../FormLegendLabel/FormLegendLabel';
import {
  FORM_LEGEND_CLASSES,
  FORM_SECTION_BODY_CLASSES,
  FORM_SECTION_CHEVRON_CLASSES,
  FORM_SECTION_CHEVRON_OPEN_CLASSES,
  FORM_SECTION_CLASSES,
  FORM_SECTION_DESCRIPTION_CLASSES,
  FORM_SECTION_TRIGGER_CLASSES,
} from '../dynamic-form.tokens';

import { type DynamicFormSectionProps } from './DynamicFormSection.types';
import { useDynamicFormSectionLogic } from './useDynamicFormSectionLogic';

/**
 * One named group of fields — a `<fieldset>` with a `<legend>`.
 *
 * Four decisions worth keeping:
 *
 * 1. **A section with no title renders no fieldset.** A `<fieldset>` without a
 *    `<legend>` is an unnamed group: a screen reader announces "group" and the
 *    operator learns nothing. A flat field list is therefore a plain `<div>`,
 *    which is also what every form in the portal was before this existed.
 * 2. **The `<legend>` is always the fieldset's FIRST child.** A legend nested
 *    inside anything — a Collapsible root, a header div — stops being the
 *    group's name and becomes an ordinary element, silently. That is why the
 *    collapsible variant renders the Collapsible root AS the fieldset
 *    (`asChild`) rather than around it, and why it cannot delegate to
 *    `FormFieldset` the way the plain variant does.
 * 3. **The UA reset lives in the tokens, not here.** A browser still gives every
 *    fieldset a border, an inline margin and a legend its own padding; the
 *    `border-0 p-0` in `FORM_SECTION_CLASSES` is what stops the portal looking
 *    like a 1997 form, and it is written logically so RTL is unaffected.
 * 4. **The disclosure IS the section's name**, so the control that folds the
 *    group is the thing that names it — and an invalid section refuses to stay
 *    folded (`useDynamicFormSectionLogic`).
 */
export function DynamicFormSection({
  section,
  isInvalid = false,
  children,
}: DynamicFormSectionProps) {
  const generatedId = useId();
  const descriptionId = `${section.id}-${generatedId}-description`;
  const { isOpen, setOpen } = useDynamicFormSectionLogic({ section, isInvalid });

  // Both halves, though `resolveSections` derives one from the other: the type
  // cannot narrow `title` through `isImplicit`, and a fieldset that renders
  // without a name is exactly what rule 1 above forbids.
  if (section.isImplicit || section.title === undefined) {
    return <div className={cn(FORM_SECTION_CLASSES, section.className)}>{children}</div>;
  }

  if (!section.isCollapsible) {
    return (
      <FormFieldset
        id={section.id}
        title={section.title}
        isInvalid={isInvalid}
        {...(section.description === undefined ? {} : { description: section.description })}
        {...(section.className === undefined ? {} : { className: section.className })}
      >
        {children}
      </FormFieldset>
    );
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setOpen}>
      <fieldset
        id={section.id}
        aria-describedby={section.description === undefined ? undefined : descriptionId}
        className={cn(FORM_SECTION_CLASSES, section.className)}
      >
        <legend className={FORM_LEGEND_CLASSES}>
          <CollapsibleTrigger className={FORM_SECTION_TRIGGER_CLASSES}>
            <ChevronDown
              aria-hidden
              className={cn(
                'h-4 w-4',
                FORM_SECTION_CHEVRON_CLASSES,
                isOpen && FORM_SECTION_CHEVRON_OPEN_CLASSES,
              )}
            />
            <FormLegendLabel title={section.title} isInvalid={isInvalid} />
          </CollapsibleTrigger>
        </legend>
        <CollapsibleContent>
          <div className={FORM_SECTION_BODY_CLASSES}>
            {section.description === undefined ? null : (
              <p id={descriptionId} className={FORM_SECTION_DESCRIPTION_CLASSES}>
                {section.description}
              </p>
            )}
            {children}
          </div>
        </CollapsibleContent>
      </fieldset>
    </Collapsible>
  );
}
